import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { SharedService } from 'src/app/services/shared.service';
import { PagesService } from '@services/pages.service';
import { TranslateModule } from '@ngx-translate/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { FormsModule } from '@angular/forms';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FilterConfig, FilterModalComponent } from '@pages/filter-modal/filter-modal.component';

@Component({
  selector: 'app-contest-monitoring',
  standalone: true,
  imports: [
    RouterLink,
    CommonModule,
    NzTableModule,
    TranslateModule,
    FormsModule,
    FilterModalComponent
  ],
  templateUrl: './contest-monitoring.component.html',
  styleUrls: ['./contest-monitoring.scss'],
})
export class ContestMonitoringComponent implements OnInit {
  contest_monitoring: any[] = [];
  listOfData: any[] = [];
  pageSize = 6;
  currentPage = 1;
  totalRecords = 0;
  fullListOfData: any[] = [];
  searchTerm: string = '';
  filteredListOfData: any[] = [];
  // Filter related properties
  filterModalVisible = false;
  currentFilters: any = {};
  filterConfig: FilterConfig = {
    showTimeline: true,
    showLevel: true,
    showStatus: true,
    showFormat: true,
    showType: true,
    showViewType: true,
    showLocation: true
  };

  http = inject(HttpClient);
  router = inject(Router);
  sharedService = inject(SharedService);
  pageService = inject(PagesService);
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;

  columns = [
    { key: 'contestName', name: 'addContestsTable.contestName', width: '200px' },
    { key: 'contestFormat', name: 'addContestsTable.contestFormat', width: '200px' },
    { key: 'contestType', name: 'addContestsTable.contestType', width: '200px' },
    { key: 'numberOfLevels', name: 'addContestsTable.numberOfLevels', width: '200px' },
    { key: 'location', name: 'addContestsTable.location', width: '200px' },
    { key: 'prize', name: 'addContestsTable.prize', width: '200px' },
    { key: 'gender', name: 'addContestsTable.gender', width: '200px' },
    { key: 'ageBreakdown', name: 'addContestsTable.ageBreakdown', width: '200px' },
    { key: 'action', name: 'addContestsTable.action', width: '200px', fixed: 'right' },
  ];

  ngOnInit(): void {
    this.getContestData();
  }

  Math = Math;

  // Remove time filter functionality - keep for statistics only
  selectedTimeFilter: 'week' | 'month' | 'year' = 'week';

  previousData: any = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    totalViews: 0,
    totalClicks: 0
  };

  // Modified to fetch ALL contests
  getContestData() {
    this.sharedService.showLoader();

    // Get previous period dates for statistics comparison only
    const previousRange = this.getPreviousDateRange(this.selectedTimeFilter);

    // Fetch previous period data for statistics
    this.pageService.getAll('ANALYSIS_URL_Reports', {
      start_date: previousRange.start_date,
      end_date: previousRange.end_date
    }).subscribe({
      next: (previousResponse: any) => {
        const currentDate = new Date().getTime();

        this.previousData = {
          totalContests: previousResponse.length,
          activeContests: previousResponse.filter((c: any) => {
            const endDate = new Date(c.endDate).getTime();
            return c.active && endDate > currentDate;
          }).length,
          pendingContests: previousResponse.filter((c: any) => c.status === 'pending').length,
          totalViews: previousResponse.reduce((sum: number, item: any) => sum + (item.totalViews || 0), 0),
          totalClicks: previousResponse.reduce((sum: number, item: any) => sum + (item.clicks || 0), 0)
        };

        // Now fetch ALL contests without time filter
        this.pageService.getAll('ANALYSIS_URL_Reports', {}).subscribe({
          next: (response: any) => {
            this.sharedService.hideLoader();

            this.fullListOfData = response.sort((a: any, b: any) => b.id - a.id);
            this.totalRecords = this.fullListOfData.length;
            this.applyFiltersAndSearch();
            this.updateStatistics();
          },
          error: (error) => {
            this.sharedService.hideLoader();
            this.sharedService.createNotification('error', 'Failed to load contest data');
          },
        });
      },
      error: (error) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', 'Failed to load previous period data');
      }
    });
  }

  getPreviousDateRange(filter: 'week' | 'month' | 'year'): { start_date: number; end_date: number } {
    const now = new Date();
    let startDate: number;
    let endDate: number;

    if (filter === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(now.getDate() - 7);
      endDate = weekAgo.getTime();

      const twoWeeksAgo = new Date(now);
      twoWeeksAgo.setDate(now.getDate() - 14);
      startDate = twoWeeksAgo.getTime();
    } else if (filter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setDate(now.getDate() - 30);
      endDate = monthAgo.getTime();

      const twoMonthsAgo = new Date(now);
      twoMonthsAgo.setDate(now.getDate() - 60);
      startDate = twoMonthsAgo.getTime();
    } else {
      const yearAgo = new Date(now);
      yearAgo.setFullYear(now.getFullYear() - 1);
      endDate = yearAgo.getTime();

      const twoYearsAgo = new Date(now);
      twoYearsAgo.setFullYear(now.getFullYear() - 2);
      startDate = twoYearsAgo.getTime();
    }

    return { start_date: startDate, end_date: endDate };
  }

  changeTimeFilter(filter: 'week' | 'month' | 'year') {
    this.selectedTimeFilter = filter;
    this.getContestData();
  }

  updateStatistics() {
    const currentDate = new Date().getTime();
    const totalContests = this.fullListOfData.length;
    const activeContests = this.fullListOfData.filter((c: any) => {
      const endDate = new Date(c.endDate).getTime();
      return c.active && endDate > currentDate;
    }).length;
    const pendingContests = this.fullListOfData.filter((c: any) => c.status === 'pending').length;
    const totalViews = this.fullListOfData.reduce((sum: number, item: any) => sum + (item.totalViews || 0), 0);
    const totalClicks = this.fullListOfData.reduce((sum: number, item: any) => sum + (item.clicks || 0), 0);

    const percentages = {
      totalContests: this.calculatePercentageChange(this.previousData.totalContests, totalContests),
      activeContests: this.calculatePercentageChange(this.previousData.activeContests, activeContests),
      pendingContests: this.calculatePercentageChange(this.previousData.pendingContests, pendingContests),
      totalViews: this.calculatePercentageChange(this.previousData.totalViews, totalViews),
      totalClicks: this.calculatePercentageChange(this.previousData.totalClicks, totalClicks)
    };

    this.contest_monitoring = [
      {
        title: 'contestMonitoring.totalContest',
        number: totalContests,
        displayNumber: totalContests,
        percentage: percentages.totalContests,
        progressText: 'contestMonitoring.progressText',
        icon: 'assets/images/participant.svg',
      },
      {
        title: 'contestMonitoring.activeContests',
        number: activeContests,
        displayNumber: activeContests,
        percentage: percentages.activeContests,
        progressText: 'contestMonitoring.progressText',
        icon: 'assets/images/view.svg',
      },
      {
        title: 'contestMonitoring.pending',
        number: pendingContests,
        displayNumber: pendingContests,
        percentage: percentages.pendingContests,
        progressText: 'contestMonitoring.progressText',
        icon: 'assets/images/clicks.svg',
      },
      {
        title: 'contestMonitoring.views',
        number: totalViews,
        displayNumber: this.formatNumber(totalViews),
        percentage: percentages.totalViews,
        progressText: 'contestMonitoring.progressText',
        icon: 'assets/images/visitor.svg',
      },
      {
        title: 'contestMonitoring.clicks',
        number: totalClicks,
        displayNumber: this.formatNumber(totalClicks),
        percentage: percentages.totalClicks,
        progressText: 'contestMonitoring.progressText',
        icon: 'assets/images/pending.svg',
      },
    ];
  }

  calculatePercentageChange(previousValue: number, currentValue: number): number {
    if (previousValue === 0 && currentValue === 0) {
      return 0;
    }
    if (previousValue === 0 && currentValue > 0) {
      return 100;
    }
    if (previousValue > 0 && currentValue === 0) {
      return -100;
    }

    const change = ((currentValue - previousValue) / previousValue) * 100;
    const roundedChange = Math.round(change);

    if (roundedChange > 100) {
      return 100;
    } else if (roundedChange < -100) {
      return -100;
    }

    return roundedChange;
  }

  openFilterModal(): void {
    this.filterModalVisible = true;
  }

  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    this.currentPage = 1;
    this.applyFiltersAndSearch();
  }

  onFiltersReset(): void {
    this.currentFilters = {};
    this.currentPage = 1;
    this.applyFiltersAndSearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.currentFilters.timeline || this.currentFilters.level ||
      this.currentFilters.status || this.currentFilters.format ||
      this.currentFilters.type || this.currentFilters.viewType ||
      this.currentFilters.location);
  }

  clearAllFilters(): void {
    this.searchTerm = '';
    this.currentFilters = {};
    this.currentPage = 1;
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        timeline: null,
        level: null,
        status: null,
        format: null,
        type: null,
        viewType: null,
        location: []
      });
    }
    this.applyFiltersAndSearch();
  }

  applyFiltersAndSearch(): void {
    let filtered = [...this.fullListOfData];

    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter((item: any) =>
        item.contestName?.toLowerCase().includes(search) ||
        item.location?.toLowerCase().includes(search) ||
        item.contestType?.toLowerCase().includes(search) ||
        item.prize?.toLowerCase().includes(search)
      );
    }

    if (this.currentFilters.status) {
      filtered = filtered.filter((item: any) => item.status === this.currentFilters.status);
    }
    if (this.currentFilters.format) {
      filtered = filtered.filter((item: any) => item.contestFormat === this.currentFilters.format);
    }
    if (this.currentFilters.type) {
      filtered = filtered.filter((item: any) => item.contestType === this.currentFilters.type);
    }
    if (this.currentFilters.viewType) {
      filtered = filtered.filter((item: any) => item.viewType === this.currentFilters.viewType);
    }
    if (this.currentFilters.location?.length) {
      filtered = filtered.filter((item: any) =>
        this.currentFilters.location.includes(item.location)
      );
    }
    if (this.currentFilters.level) {
      filtered = filtered.filter((item: any) => item.numberOfLevels?.toString() === this.currentFilters.level);
    }
    if (this.currentFilters.timeline) {
      const now = new Date().getTime();
      filtered = filtered.filter((item: any) => {
        const startDate = new Date(item.startDate).getTime();
        const endDate = new Date(item.endDate).getTime();
        if (this.currentFilters.timeline === 'upcoming') {
          return startDate > now;
        } else if (this.currentFilters.timeline === 'ongoing') {
          return startDate <= now && endDate >= now;
        } else if (this.currentFilters.timeline === 'expired') {
          return endDate < now;
        }
        return true;
      });
    }

    this.filteredListOfData = filtered;
    this.totalRecords = filtered.length;
    this.updatePaginatedData();
  }

  onSearch(event: any): void {
    this.searchTerm = event.target.value;
    this.currentPage = 1;
    this.applyFiltersAndSearch();
  }

  formatNumber(value: number): string {
    if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (value >= 1_000) {
      return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return value.toString();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.updatePaginatedData();
  }

  updatePaginatedData() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    const dataSource = this.filteredListOfData.length > 0 || this.searchTerm || this.hasActiveFilters()
      ? this.filteredListOfData
      : this.fullListOfData;
    this.listOfData = dataSource.slice(start, end);
  }

  viewDetail(id: number) {
    this.router.navigate(['/home/analytic/monitoring'], {
      queryParams: { contest_id: id },
    });
  }

  goToAnalyticsWithState(): void {
    this.router.navigate(['/home/analytic'], { queryParams: {} });
  }

  getTimelineStatus(endDate: string): { timeline: string; status: string } {
    if (!endDate) {
      return { timeline: 'N/A', status: 'N/A' };
    }
    
    const currentDate = new Date();
    const contestEndDate = new Date(endDate);
    const timeDiff = contestEndDate.getTime() - currentDate.getTime();
    
    if (timeDiff < 0) {
      return { timeline: 'Expired', status: 'Expired' };
    }
    
    const daysRemaining = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hoursRemaining = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    let timeline = '';
    if (daysRemaining > 0) {
      timeline = `${daysRemaining}d`;
    } else if (hoursRemaining > 0) {
      timeline = `${hoursRemaining}h`;
    } else if (minutesRemaining > 0) {
      timeline = `${minutesRemaining}m`;
    } else {
      timeline = 'Ending Soon';
    }
    
    return { timeline, status: 'Active' };
  }

  downloadPDF() {
    if (!this.fullListOfData || this.fullListOfData.length === 0) {
      this.sharedService.createNotification('error', 'No Contest Data Found');
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let currentY = 15;

    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Luqta Contest Monitoring Report', pageWidth / 2, currentY, {
      align: 'center',
    });

    const bodyRows = this.fullListOfData.map((item: any) => {
      const { timeline, status } = this.getTimelineStatus(item.endDate);
      return [
        item.id || 'N/A',
        item.contestName || 'N/A',
        item.contestFormat || 'N/A',
        item.contestType || 'N/A',
        item.numberOfLevels || 0,
        item.location || 'N/A',
        item.prize || 'N/A',
        item.gender || 'N/A',
        item.ageBreakdown || 'N/A',
        timeline,
        status,
      ];
    });

    autoTable(doc, {
      startY: currentY + 10,
      head: [
        [
          'ID',
          'Contest Name',
          'Format',
          'Type',
          'Levels',
          'Location',
          'Prize',
          'Gender',
          'Age',
          'Timeline',
          'Status',
        ],
      ],
      body: bodyRows,
      styles: {
        fontSize: 8,
        cellPadding: 2.5,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        halign: 'center',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        fontSize: 9,
      },
      bodyStyles: {
        textColor: 50,
        halign: 'center',
        valign: 'middle',
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 35 },
        2: { cellWidth: 25 },
        3: { cellWidth: 20 },
        4: { cellWidth: 15 },
        5: { cellWidth: 30 },
        6: { cellWidth: 35 },
        7: { cellWidth: 18 },
        8: { cellWidth: 18 },
        9: { cellWidth: 20 },
        10: { cellWidth: 20 },
      },
      didDrawCell: (data: any) => {
        if (data.section === 'body' && data.column.index === 10) {
          const status = data.cell.raw;
          if (status === 'Expired') {
            doc.setFillColor(255, 99, 71);
            doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
            doc.setTextColor(255, 255, 255);
            doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, {
              align: 'center',
              baseline: 'middle',
            });
          } else if (status === 'Active') {
            doc.setFillColor(46, 204, 113);
            doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
            doc.setTextColor(255, 255, 255);
            doc.text(status, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, {
              align: 'center',
              baseline: 'middle',
            });
          }
        }
      },
      margin: { top: 5, left: 10, right: 10 },
      didDrawPage: function (data) {
        const pageCount = doc.internal.pages.length - 1;
        doc.setFontSize(9);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth - 20,
          pageHeight - 10,
          { align: 'right' }
        );
      },
    });
    
    const timestamp = new Date().toISOString().split('T')[0];
    doc.save(`Contest_Monitoring_Report_${timestamp}.pdf`);
    
    this.sharedService.createNotification(
      'success',
      'PDF downloaded successfully'
    );
  }

  downloadExcel() {
    if (!this.fullListOfData || this.fullListOfData.length === 0) {
      this.sharedService.createNotification('error', 'No Contest Data Found');
      return;
    }

    // Prepare data for Excel
    const excelData = this.fullListOfData.map((item: any) => {
      const { timeline, status } = this.getTimelineStatus(item.endDate);
      return {
        'ID': item.id || 'N/A',
        'Contest Name': item.contestName || 'N/A',
        'Format': item.contestFormat || 'N/A',
        'Type': item.contestType || 'N/A',
        'Levels': item.numberOfLevels || '0',
        'Location': item.location || 'N/A',
        'Prize': item.prize || 'N/A',
        'Total Views': item.totalViews || 0,
        'Total Joins': item.totalJoins || 0,
        'Completion Rate': item.completionRate || 0,
        'Winners': item.numberOfWinners || 0,
        'Clicks': item.clicks || 0,
        'Gender': item.gender || 'N/A',
        'Age Breakdown': item.ageBreakdown || 'All',
        'Timeline': timeline,
        'Status': status,
      };
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const columnWidths = [
      { wch: 8 },   // ID
      { wch: 25 },  // Contest Name
      { wch: 15 },  // Format
      { wch: 15 },  // Type
      { wch: 8 },   // Levels
      { wch: 20 },  // Location
      { wch: 20 },  // Prize
      { wch: 12 },  // Total Views
      { wch: 12 },  // Total Joins
      { wch: 15 },  // Completion Rate
      { wch: 10 },  // Winners
      { wch: 10 },  // Clicks
      { wch: 10 },  // Gender
      { wch: 15 },  // Age Breakdown
      { wch: 12 },  // Timeline
      { wch: 10 },  // Status
    ];
    worksheet['!cols'] = columnWidths;

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contest Report');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    // Save file
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Contest_Monitoring_Report_${timestamp}.xlsx`;
    saveAs(blob, filename);

    this.sharedService.createNotification(
      'success',
      'Excel downloaded successfully'
    );
  }

  onMouseEnter(event: MouseEvent, value: string) {
    if (!value || value.length <= 12) return;
    const container = event.currentTarget as HTMLElement;
    const tooltip = container.querySelector('.custom-tooltip') as HTMLElement;
    if (tooltip) {
      const rect = container.getBoundingClientRect();
      const left = rect.left + (rect.width / 2);
      const top = rect.top - 8;
      tooltip.style.position = 'fixed';
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      tooltip.style.transform = 'translate(-50%, -100%)';
      tooltip.style.visibility = 'visible';
      tooltip.style.opacity = '1';
      tooltip.style.zIndex = '9999';
    }
  }

  onMouseLeave(event: MouseEvent) {
    const container = event.currentTarget as HTMLElement;
    const tooltip = container.querySelector('.custom-tooltip') as HTMLElement;
    if (tooltip) {
      tooltip.style.visibility = 'hidden';
      tooltip.style.opacity = '0';
    }
  }
}